import { View, Pressable, ActivityIndicator } from "react-native";
import { RetellWebClient } from "retell-client-js-sdk";
import { memo, useCallback, useEffect, useState } from "react";
import { useFocusEffect } from "expo-router";
import { usePostHog } from "posthog-js/react";
import { useSettingsService } from "../../../services/api/settings-service";
import { useToast } from "../../../contexts/ToastContext";
import { useActiveAgent } from "../../../contexts/ActiveAgentContext";
import { useSettingsData } from "../../../contexts/SettingsDataContext";
import StyledText from "../../common/ui/styled-text";
import { PhoneCall, Square } from "lucide-react-native";

const retell = new RetellWebClient();

function TestCallButton() {
  const {
    isTestCallActive,
    handleTestCallActive,
    isLoading: isSettingsLoading,
  } = useSettingsData();
  const { activeAgent } = useActiveAgent();

  const settingsService = useSettingsService();
  const { showErrorToast } = useToast();
  const posthog = usePostHog();

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (activeAgent) {
      stopCall();
    }
  }, [activeAgent]);

  useEffect(() => {
    return () => {
      stopCall();
    };
  }, []);

  useFocusEffect(
    useCallback(() => {
      return async () => {
        stopCall();
      };
    }, [])
  );

  useEffect(() => {
    retell.on("retell call_ended", () => {
      handleTestCallActive(false);
    });
    retell.on("retell error", (error) => {
      console.error(`A test call error occurred: ${error}`);
      stopCall();
    });
  }, []);

  const toggleTestCall = async () => {
    try {
      if (isTestCallActive) {
        posthog.capture("test_call_toggled", {
          is_active: false,
        });
        stopCall();
      } else {
        posthog.capture("test_call_toggled", {
          is_active: true,
        });
        setIsLoading(true);
        const permission = await getMicrophonePermissionOtherwiseRequestIt();
        if (permission) {
          const response = await settingsService.startTestCall();
          if (response.accessToken) {
            await retell.startCall({
              accessToken: response.accessToken,
            });
            handleTestCallActive(true);
          }
          setIsLoading(false);
        } else {
          posthog.capture("test_call_error", {
            error: "Microphone permission not granted",
          });
          setIsLoading(false);
          showErrorToast(
            "Microphone permission is required to test your A.I. assistant."
          );
        }
      }
    } catch (error) {
      posthog.capture("test_call_error", {
        error: error,
      });
      showErrorToast(
        "We're sorry, but we couldn't start your test call at this time."
      );
      stopCall();
    }
  };

  const stopCall = () => {
    setIsLoading(false);
    retell.stopCall();
    handleTestCallActive(false);
  };

  const getMicrophonePermissionOtherwiseRequestIt = async () => {
    try {
      if (!navigator?.mediaDevices?.getUserMedia) {
        throw new Error("getUserMedia not supported");
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop());
      return true;
    } catch (error) {
      console.error("Error requesting microphone permission:", error);
      return false;
    }
  };

  return (
    <View className="flex-col justify-center items-center">
      <Pressable
        onPress={toggleTestCall}
        className={`rounded-md border shadow-xs py-2 px-3 items-center ${
          isTestCallActive
            ? "bg-red-500 hover:bg-red-600 border-red-600"
            : "bg-gray-50 hover:bg-gray-100 border-gray-200"
        }`}
        disabled={isLoading || isSettingsLoading}
      >
        {isLoading ? (
          <ActivityIndicator
            size="small"
            className="w-16"
            color={isTestCallActive ? "white" : "black"}
          />
        ) : isTestCallActive ? (
          <View className="flex-row items-center gap-1.5">
            <Square size={10} color="white" />
            <StyledText className="font-medium text-xs text-white">
              Stop Call
            </StyledText>
          </View>
        ) : (
          <View className="flex-row items-center gap-1.5">
            <PhoneCall size={10} color="#18181B" />
            <StyledText className="font-medium text-xs">Test Call</StyledText>
          </View>
        )}
      </Pressable>
    </View>
  );
}

export default memo(TestCallButton);
